<template>
  <div class="podcast-charts">
    <h1 class="bold">Podcast Charts</h1>

    <!-- Navigation Tabs -->
    <div class="tab-navigation">
      <button v-for="tab in tabs" :key="tab.key" :class="['tab-btn', { active: activeTab === tab.key }]"
        @click="activeTab = tab.key">
        {{ tab.label }}
      </button>
    </div>

    <!-- Reports Tab -->
    <div v-if="activeTab === 'overview'" class="tab-content reports">
      <div class="controls-section">
        <div class="week-year-controls">
          <div class="date-picker-container">
            <VueDatePicker 
              v-model="selectedDate" 
              :min-date="minDate" 
              :max-date="maxDate" 
              :enable-time-picker="false" 
              placeholder="Select a week" 
              week-picker 
              auto-apply />
          </div>
          <button class="purple-btn" @click="fetchReports"
            :disabled="isLoadingReports || !selectedDate">
            Get Reports
          </button>
        </div>
      </div>

      <div v-if="isLoadingReports" class="loading-section">
        <span class="spinner" />
        <span>Loading reports...</span>
      </div>


      <div v-else-if="reports && Object.keys(reports).length > 0" class="data-section">
        <div class="summary-section">
          <h2>Scraping Report Summary</h2>
          <p v-if="reports.week && reports.year">Week {{ reports.week }}, {{ reports.year }}</p>

          <div class="summary-grid" v-if="reports.summary">
            <div class="summary-card">
              <div class="summary-number">{{ reports.summary.totalPodcasts?.toLocaleString() || 0 }}</div>
              <div class="summary-label">Total Podcasts</div>
            </div>
            <div class="summary-card">
              <div class="summary-number">{{ reports.summary.platformsCount || 0 }}</div>
              <div class="summary-label">Platforms</div>
            </div>
            <div class="summary-card">
              <div class="summary-number">{{ reports.summary.countriesCount || 0 }}</div>
              <div class="summary-label">Countries</div>
            </div>
            <div class="summary-card">
              <div class="summary-number">{{ reports.summary.coverage?.coveragePercentage || 0 }}%</div>
              <div class="summary-label">Coverage</div>
            </div>
          </div>
        </div>

        <div class="platforms-section" v-if="reports.platforms">
          <div class="platform-tabs">
            <button v-for="platform in Object.keys(reports.platforms)" :key="platform"
              :class="['platform-tab', { active: activePlatformReport === platform }]"
              @click="activePlatformReport = platform">
              {{ platform.charAt(0).toUpperCase() + platform.slice(1) }}
            </button>
          </div>

          <!-- Controls -->
          <div class="controls-bar">
            <input type="text" class="search-input" placeholder="Search countries or categories..."
              v-model="reportSearchQuery">

            <select class="filter-select" v-model="reportStatusFilter">
              <option value="all">All Status</option>
              <option value="scraped">Scraped Only</option>
              <option value="not-scraped">Not Scraped Only</option>
            </select>

            <select class="filter-select" v-model="reportSortBy">
              <option value="country">Sort by Country</option>
              <option value="podcasts">Sort by Podcast Count</option>
              <option value="categories">Sort by Categories</option>
            </select>

            <div class="status-legend">
              <div class="legend-item">
                <div class="legend-dot scraped"></div>
                <span>Scraped</span>
              </div>
              <div class="legend-item">
                <div class="legend-dot not-scraped"></div>
                <span>Not Scraped</span>
              </div>
            </div>
          </div>

          <!-- Countries Data -->
          <div class="countries-grid">
            <div v-for="(countryData, countryName) in filteredCountriesData"
              :key="`${activePlatformReport}-${countryName}`" class="country-card">
              <div class="country-header">
                <div class="country-name">
                  {{ getCountryFlag(countryName) }} {{ getCountryDisplayName(countryName) }}
                </div>
                <div class="country-stats">
                  <div class="stat-item">
                    <span>📊</span>
                    <span>{{ getScrapedCount(countryData) }}/{{ getTotalCount(countryData) }} scraped</span>
                  </div>
                  <div class="stat-item">
                    <span>🎧</span>
                    <span>{{ getPodcastCount(countryData) }} podcasts</span>
                  </div>
                </div>
              </div>

              <div v-if="hasAnyScrapedData(countryData)" class="categories-grid">
                <div v-for="(categoryData, categoryName) in getFilteredCategories(countryData)" :key="categoryName"
                  :class="['category-item', categoryData.scraped ? 'scraped' : 'not-scraped']">
                  <div class="category-info">
                    <div class="category-name">{{ formatCategoryName(categoryName) }}</div>
                    <div class="category-id">ID: {{ categoryData.categoryId || 'N/A' }}</div>
                  </div>
                  <div class="category-metrics">
                    <div class="podcast-count">{{ categoryData.podcastsNo || 0 }}</div>
                    <div :class="['status-badge', categoryData.scraped ? 'scraped' : 'not-scraped']">
                      {{ categoryData.scraped ? 'Scraped' : 'Not Scraped' }}
                    </div>
                  </div>
                </div>
              </div>

              <div v-else class="empty-state">
                <div class="empty-state-icon">🚫</div>
                <div>No data scraped for this country</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Missing Data Tab -->
    <div v-if="activeTab === 'details'" class="tab-content missing-data">
      <div v-if="isLoadingMissingData" class="loading-section">
        <span class="spinner" />
        <span>Loading missing data...</span>
      </div>

      <div v-else-if="missingData" class="data-section">
        <div class="summary-cards">
          <div class="card">
            <h3>Overall Coverage</h3>
            <div class="metric">
              <span class="value">{{ missingData.overallSummary?.overallCoveragePercentage || 0 }}%</span>
              <span class="label">Total Coverage</span>
            </div>
            <div class="details">
              <span>{{ missingData.overallSummary?.totalFoundCombinations || 0 }} / {{
                missingData.overallSummary?.totalExpectedCombinations || 0 }} combinations found</span>
            </div>
          </div>

          <div v-for="platform in Object.keys(missingData.platforms || {})" :key="platform" class="card">
            <h3>{{ platform.charAt(0).toUpperCase() + platform.slice(1) }}</h3>
            <div class="metric">
              <span class="value">{{ missingData.platforms[platform].coveragePercentage }}%</span>
              <span class="label">Coverage</span>
            </div>
            <div class="details">
              <span>{{ missingData.platforms[platform].foundCombinations }} / {{
                missingData.platforms[platform].expectedCombinations }} combinations</span>
            </div>
          </div>
        </div>

        <div class="platform-tabs">
          <button v-for="platform in availablePlatforms" :key="platform"
            :class="['platform-tab', { active: activePlatform === platform }]" @click="activePlatform = platform">
            {{ platform.charAt(0).toUpperCase() + platform.slice(1) }}
            <span class="badge">
              {{ missingData.platforms[platform].missingCombinations.length }}
            </span>
          </button>
        </div>

        <div v-if="activePlatform && missingData.platforms?.[activePlatform]" class="content">
          <div class="table-header">
            <h3>{{ activePlatform.charAt(0).toUpperCase() + activePlatform.slice(1) }} - Missing Combinations</h3>
            <div class="table-info">
              <span>{{ missingData.platforms[activePlatform]?.missingCombinations?.length || 0 }} missing
                combinations</span>
              <span>{{ missingData.platforms[activePlatform]?.coveragePercentage || 0 }}% coverage</span>
              <span v-if="missingData.platforms[activePlatform].week">Week {{ missingData.platforms[activePlatform].week
                }}, {{ missingData.platforms[activePlatform].year }}</span>
            </div>
          </div>

          <div class="table-container">
            <table>
              <thead>
                <tr>
                  <th>Country</th>
                  <th>Category</th>
                  <th v-if="missingData.platforms[activePlatform].missingCombinations?.[0]?.week">Week</th>
                  <th v-if="missingData.platforms[activePlatform].missingCombinations?.[0]?.year">Year</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(combination, index) in missingData.platforms[activePlatform].missingCombinations || []"
                  :key="index">
                  <td>
                    <span class="country-code">{{ combination.country?.code }}</span>
                    <span class="country-name">{{ combination.country?.name }}</span>
                  </td>
                  <td>
                    <span class="category-name">{{ combination.category?.name }}</span>
                    <span class="category-id">{{ combination.category?.id }}</span>
                  </td>
                  <td v-if="combination.week">{{ combination.week }}</td>
                  <td v-if="combination.year">{{ combination.year }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- Configuration Tab -->
    <div v-if="activeTab === 'config'" class="tab-content config">
      <div v-if="isLoadingConfig" class="loading-section">
        <span class="spinner" />
        <span>Loading configuration...</span>
      </div>

      <div v-else>
        <form @submit.prevent="handleConfigUpdate">
          <div class="model-info">
            <BaseInput name="model" label="GPT Model" placeholder="Provide GPT model name" :disabled="isSavingConfig"
              :value="config.model" @input="handleConfigInput" />
            <BaseInput name="temperature" label="GPT Model Temperature" :disabled="isSavingConfig"
              placeholder="Provide GPT model temperature" :value="config.temperature" @input="handleConfigInput" />
          </div>
          <BaseInput name="systemPrompt" label="System Prompt" :disabled="isSavingConfig"
            placeholder="Provide system prompt" type="textarea" :value="config.systemPrompt"
            @input="handleConfigInput" />
          <BaseInput name="userPrompt" label="User Prompt" :disabled="isSavingConfig" placeholder="Provide user prompt"
            type="textarea" :value="config.userPrompt" @input="handleConfigInput" />
          <button class="purple-btn" :disabled="isSavingConfig" type="submit">
            <span class="spinner spinner--white" v-if="isSavingConfig" />
            {{ isSavingConfig ? ' Saving...' : 'Save changes' }}
          </button>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from "vue";
import { useStore } from "vuex";
import { endOfWeek, getISOWeek, getISOWeekYear } from "date-fns";
import VueDatePicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';
import BaseInput from "@/components/BaseInput.vue";

const { state, commit, dispatch } = useStore();

dispatch('get_podcast_charts_missing_data');
dispatch('get_podcast_charts_config');

const activeTab = ref('overview');
const tabs = [
  { key: 'overview', label: 'Reports' },
  { key: 'details', label: 'Missing Data' },
  { key: 'config', label: 'Configuration' }
];

// Reports

const minDate = new Date(2025, 3, 1)
const maxDate = endOfWeek(new Date())
const selectedDate = ref(null);
const activePlatformReport = ref('apple');
const reportSearchQuery = ref('');
const reportStatusFilter = ref('all');
const reportSortBy = ref('country');
const reports = computed(() => state.podcastCharts.reports);
const isLoadingReports = computed(() => state.podcastCharts.loading.has('get_podcast_charts_reports'));
const filteredCountriesData = computed(() => {
  if (!reports.value?.platforms?.[activePlatformReport.value]) return {};
  const platformData = reports.value.platforms[activePlatformReport.value];
  let filtered = { ...platformData };
  if (reportSearchQuery.value) {
    const searchTerm = reportSearchQuery.value.toLowerCase();
    filtered = Object.fromEntries(
      Object.entries(filtered).filter(([countryName, countryData]) => {
        if (countryName.toLowerCase().includes(searchTerm)) return true;
        return Object.keys(countryData).some(categoryName => 
          categoryName.toLowerCase().includes(searchTerm)
        );
      })
    );
  }
  if (reportStatusFilter.value !== 'all') {
    filtered = Object.fromEntries(
      Object.entries(filtered).filter(([countryName, countryData]) => {
        return Object.values(countryData).some(categoryData => {
          if (reportStatusFilter.value === 'scraped') {
            return categoryData.scraped;
          } else {
            return !categoryData.scraped;
          }
        });
      })
    );
  }
  if (reportSortBy.value) {
    filtered = Object.fromEntries(
      Object.entries(filtered).sort(([countryA], [countryB]) => {
        if (reportSortBy.value === 'country') {
          return countryA.localeCompare(countryB);
        } else if (reportSortBy.value === 'podcasts') {
          const countA = getPodcastCount(filtered[countryA]);
          const countB = getPodcastCount(filtered[countryB]);
          return countB - countA;
        } else if (reportSortBy.value === 'categories') {
          const countA = Object.keys(filtered[countryA]).length;
          const countB = Object.keys(filtered[countryB]).length;
          return countB - countA; 
        }
        return 0;
      })
    );
  }
  return filtered;
});

function getCountryFlag(countryName) {
  const flagMap = {
    'Germany(DE)': '🇩🇪',
    'France(FR)': '🇫🇷',
    'India(IN)': '🇮🇳',
    'Indonesia(ID)': '🇮🇩',
    'Ireland(IE)': '🇮🇪',
    'Israel(IL)': '🇮🇱',
    'Italy(IT)': '🇮🇹',
    'Japan(JP)': '🇯🇵',
    'Mexico(MX)': '🇲🇽',
    'New Zealand(NZ)': '🇳🇿',
    'Norway(NO)': '🇳🇴',
    'Philippines(PH)': '🇵🇭',
    'Poland(PL)': '🇵🇱',
    'Spain(ES)': '🇪🇸',
    'Sweden(SE)': '🇸🇪',
    'Netherlands(NL)': '🇳🇱',
    'United Kingdom(GB)': '🇬🇧',
    'United States(US)': '🇺🇸',
    'Argentina(AR)': '🇦🇷',
    'Australia(AU)': '🇦🇺',
    'Austria(AT)': '🇦🇹',
    'Brazil(BR)': '🇧🇷',
    'Canada(CA)': '🇨🇦',
    'Chile(CL)': '🇨🇱',
    'Colombia(CO)': '🇨🇴',
    'Denmark(DK)': '🇩🇰',
    'Finland(FI)': '🇫🇮'
  };
  return flagMap[countryName] || '🏳️';
}

function getCountryDisplayName(countryName) {
  return countryName.replace(/\([^)]*\)/, '').trim();
}

function getScrapedCount(countryData) {
  return Object.values(countryData).filter(category => category.scraped).length;
}

function getTotalCount(countryData) {
  return Object.keys(countryData).length;
}

function getPodcastCount(countryData) {
  return Object.values(countryData).reduce((sum, category) => sum + (category.podcastsNo || 0), 0);
}

function hasAnyScrapedData(countryData) {
  return Object.values(countryData).some(category => category.scraped);
}

function getFilteredCategories(countryData) {
  let categories = { ...countryData };
  if (reportStatusFilter.value === 'scraped') {
    categories = Object.fromEntries(
      Object.entries(categories).filter(([_, categoryData]) => categoryData.scraped)
    );
  } else if (reportStatusFilter.value === 'not-scraped') {
    categories = Object.fromEntries(
      Object.entries(categories).filter(([_, categoryData]) => !categoryData.scraped)
    );
  }
  return categories;
}

function formatCategoryName(categoryName) {
  return categoryName
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

async function fetchReports() {
  dispatch('get_podcast_charts_reports', {
    week: getISOWeek(selectedDate.value[0]),
    year: getISOWeekYear(selectedDate.value[0])
  })
}

// Missing Data

const activePlatform = ref('apple');
const missingData = computed(() => state.podcastCharts.missingData);
const isLoadingMissingData = computed(() => state.podcastCharts.loading.has('get_podcast_charts_missing_data'));
const availablePlatforms = computed(() => {
  if (missingData.value.platforms) {
    return Object.keys(missingData.value.platforms);
  }
  return ['apple', 'spotify', 'youtube'];
});

// Configuration

const config = computed(() => state.podcastCharts.config);
const isLoadingConfig = computed(() => state.podcastCharts.loading.has('get_podcast_charts_config'));
const isSavingConfig = computed(() => state.podcastCharts.loading.has('update_podcast_charts_config'));

function handleConfigInput({ name, value }) {
  commit('SET_PODCAST_CHARTS_STATE', {
    key: "config",
    value: {
      ...config.value,
      [name]: value
    }
  });
}

function handleConfigUpdate() {
  dispatch('update_podcast_charts_config');
}
</script>

<style lang="scss">
.podcast-charts {
  @media only screen and (min-width: 0) {
    padding-bottom: 16px;

    .tab-navigation {
      display: flex;
      gap: 8px;
      margin-bottom: 24px;
      border-bottom: 1px solid #e5e7eb;

      .tab-btn {
        padding: 12px 24px;
        background: none;
        border: none;
        border-bottom: 2px solid transparent;
        cursor: pointer;
        font-weight: 500;
        color: #6b7280;
        transition: all 0.2s;

        &:hover {
          color: #374151;
        }

        &.active {
          color: #7e3af2;
          border-bottom-color: #7e3af2;
        }
      }
    }

    .tab-content.reports .controls-section {
      display: flex;
      flex-direction: column;
      gap: 16px;
      margin-bottom: 24px;
      padding: 20px;
      background: #f9fafb;
      border-radius: 8px;

      .week-year-controls {
        display: flex;
        gap: 16px;
        align-items: end;

        .dp__theme_light {
          --dp-primary-color: #7e3af2;
          --dp-border-radius: 8px;
          --dp-border-color: #d1d5db;
        }

        input {
          height: 44px;
          width: 304px;
          font-size: 14px;
        }

        svg {
          width: 18px;
          height: 18px;
        }
      }
    }

    .tab-content.reports .data-section {
      margin-top: 32px;

      .summary-section {
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        color: white;
        padding: 24px;
        border-radius: 12px;
        margin-bottom: 24px;

        h2 {
          color: white;
          margin: 0 0 8px 0;
          font-size: 24px;
          font-weight: 600;
        }

        p {
          margin: 0;
          opacity: 0.9;
          font-size: 14px;
        }
      }

      .summary-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
        gap: 20px;
        margin-top: 16px;
      }

      .summary-card {
        background: rgba(255, 255, 255, 0.1);
        padding: 16px;
        border-radius: 8px;
        backdrop-filter: blur(10px);

        .summary-number {
          font-size: 24px;
          font-weight: 700;
          margin-bottom: 4px;
        }

        .summary-label {
          font-size: 14px;
          opacity: 0.9;
        }
      }

      .platforms-section {
        margin-bottom: 24px;
      }

      .platform-tabs {
        display: flex;
        gap: 4px;
        margin-bottom: 20px;
        border-bottom: 1px solid #e5e7eb;
      }

      .platform-tab {
        padding: 12px 24px;
        background: none;
        border: none;
        border-bottom: 2px solid transparent;
        cursor: pointer;
        font-weight: 500;
        color: #6b7280;
        transition: all 0.2s;
        text-transform: capitalize;

        &:hover {
          color: #374151;
          background: #f9fafb;
        }

        &.active {
          color: #7e3af2;
          border-bottom-color: #7e3af2;
          background: #f3f4f6;
        }
      }

      .controls-bar {
        display: flex;
        gap: 16px;
        margin-bottom: 20px;
        padding: 16px;
        background: #f9fafb;
        border-radius: 8px;
        align-items: center;
        flex-wrap: wrap;

        .search-input {
          flex: 1;
          min-width: 200px;
          padding: 8px 12px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          font-size: 14px;
        }

        .filter-select {
          padding: 8px 12px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          background: white;
          font-size: 14px;
          min-width: 120px;
        }

        .status-legend {
          display: flex;
          gap: 16px;
          align-items: center;

          .legend-item {
            display: flex;
            align-items: center;
            gap: 6px;
            font-size: 12px;

            .legend-dot {
              width: 12px;
              height: 12px;
              border-radius: 50%;

              &.scraped {
                background: #10b981;
              }

              &.not-scraped {
                background: #ef4444;
              }
            }
          }
        }
      }

      .countries-grid {
        display: grid;
        gap: 20px;
      }

      .country-card {
        background: white;
        border: 1px solid #e5e7eb;
        border-radius: 12px;
        overflow: hidden;
        transition: transform 0.2s, box-shadow 0.2s;

        &:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
        }

        .country-header {
          padding: 16px 20px;
          background: #f8fafc;
          border-bottom: 1px solid #e5e7eb;
          display: flex;
          justify-content: space-between;
          align-items: center;

          .country-name {
            font-weight: 600;
            font-size: 16px;
            color: #1f2937;
          }

          .country-stats {
            display: flex;
            gap: 16px;
            font-size: 12px;
            color: #6b7280;

            .stat-item {
              display: flex;
              align-items: center;
              gap: 4px;
            }
          }
        }

        .categories-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 12px;
          padding: 20px;
        }

        .category-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 12px;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          transition: all 0.2s;

          &:hover {
            background: #f9fafb;
          }

          &.scraped {
            border-left: 4px solid #10b981;
            background: #f0fdf4;
          }

          &.not-scraped {
            border-left: 4px solid #ef4444;
            background: #fef2f2;
          }

          .category-info {
            flex: 1;

            .category-name {
              font-weight: 500;
              color: #1f2937;
              margin-bottom: 2px;
            }

            .category-id {
              font-size: 11px;
              color: #6b7280;
              font-family: 'Monaco', 'Menlo', monospace;
            }
          }

          .category-metrics {
            display: flex;
            align-items: center;
            gap: 12px;

            .podcast-count {
              font-weight: 600;
              color: #1f2937;
            }

            .status-badge {
              padding: 4px 8px;
              border-radius: 4px;
              font-size: 11px;
              font-weight: 500;
              text-transform: uppercase;

              &.scraped {
                background: #d1fae5;
                color: #065f46;
              }

              &.not-scraped {
                background: #fee2e2;
                color: #991b1b;
              }
            }
          }
        }

        .empty-state {
          text-align: center;
          padding: 40px;
          color: #6b7280;

          .empty-state-icon {
            font-size: 48px;
            margin-bottom: 16px;
          }
        }
      }

      @media (max-width: 768px) {
        .summary-grid {
          grid-template-columns: repeat(2, 1fr);
        }

        .controls-bar {
          flex-direction: column;
          align-items: stretch;
        }

        .categories-grid {
          grid-template-columns: 1fr;
        }

        .platform-tabs {
          flex-wrap: wrap;
        }
      }
    }

    .tab-content.missing-data .data-section {
      .summary-cards {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
        gap: 20px;
        margin-bottom: 32px;

        .card {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          padding: 24px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

          h3 {
            margin: 0 0 16px 0;
            color: #1f2937;
            font-size: 18px;
            font-weight: 600;
          }

          .metric {
            margin-bottom: 12px;

            .value {
              display: block;
              font-size: 32px;
              font-weight: 700;
              color: #7e3af2;
              line-height: 1;
            }

            .label {
              display: block;
              font-size: 14px;
              color: #6b7280;
              margin-top: 4px;
            }
          }

          .details {
            font-size: 14px;
            color: #6b7280;
            margin-bottom: 16px;
          }

          .platform-btn {
            width: 100%;
            padding: 8px 16px;
            background: #f3f4f6;
            border: 1px solid #d1d5db;
            border-radius: 6px;
            cursor: pointer;
            font-size: 14px;
            transition: all 0.2s;

            &:hover:not(:disabled) {
              background: #e5e7eb;
            }

            &:disabled {
              opacity: 0.5;
              cursor: not-allowed;
            }
          }
        }
      }

      .platform-tabs {
        display: flex;
        gap: 8px;
        margin-bottom: 24px;

        .platform-tab {
          padding: 10px 16px;
          background: #f3f4f6;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          cursor: pointer;
          font-weight: 500;
          color: #374151;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          gap: 8px;

          &:hover {
            background: #e5e7eb;
          }

          &.active {
            background: #7e3af2;
            color: white;
            border-color: #7e3af2;
          }

          .badge {
            background: rgba(255, 255, 255, 0.2);
            padding: 2px 6px;
            border-radius: 4px;
            font-size: 12px;
            font-weight: 600;
          }
        }
      }

      .content .table-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 16px;

        h3 {
          margin: 0;
          color: #1f2937;
        }

        .table-info {
          display: flex;
          gap: 16px;
          font-size: 14px;
          color: #6b7280;

          span {
            background: #f3f4f6;
            padding: 4px 8px;
            border-radius: 4px;
          }
        }
      }

      .content .table-container {
        background: white;
        border: 1px solid #e5e7eb;
        border-radius: 8px;
        overflow: hidden;

        table {
          width: 100%;
          border-collapse: collapse;

          thead {
            background: #f9fafb;

            th {
              padding: 12px 16px;
              text-align: left;
              font-weight: 600;
              color: #374151;
              border-bottom: 1px solid #e5e7eb;
            }
          }

          tbody {
            tr {
              border-bottom: 1px solid #f3f4f6;

              &:hover {
                background: #f9fafb;
              }

              td {
                padding: 12px 16px;
                vertical-align: top;

                .country-code {
                  display: inline-block;
                  background: #7e3af2;
                  color: white;
                  padding: 2px 6px;
                  border-radius: 4px;
                  font-size: 12px;
                  font-weight: 600;
                  margin-right: 8px;
                }

                .country-name {
                  color: #1f2937;
                  font-weight: 500;
                }

                .category-name {
                  display: block;
                  color: #1f2937;
                  font-weight: 500;
                }

                .category-id {
                  display: block;
                  color: #6b7280;
                  font-size: 12px;
                  margin-top: 2px;
                }
              }
            }
          }
        }
      }
    }

    .tab-content.config form {
      display: flex;
      flex-direction: column;
      gap: 20px;
      padding: 24px 0;

      .model-info {
        display: flex;
        gap: 20px;

        .base-input {
          width: 100%;
        }
      }

      textarea {
        min-height: 184px;
      }
    }

    .purple-btn {
      height: 44px;
      background: #7e3af2;
      color: white;
      border: none;
      padding: 12px 24px;
      border-radius: 8px;
      cursor: pointer;
      font-weight: 500;
      transition: background 0.2s;
      display: flex;
      justify-content: center;
      align-items: center;

      &:hover:not(:disabled) {
        background: #6d28d9;
      }

      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
    }

    .spinner {
      display: inline-block;
      width: 20px;
      height: 20px;
      margin-right: 10px;
      border-radius: 50%;
      border: 2px solid rgba(0, 0, 0, 0.1);
      border-top-color: #7e3af2;
      animation: spin 0.8s linear infinite;

      &--white {
        border-top-color: white;
      }
    }

    .loading-section {
      width: 100%;
      height: 284px;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      gap: 16px;

      .spinner {
        width: 48px;
        height: 48px;
      }
    }

    @keyframes spin {
      to {
        transform: rotate(360deg);
      }
    }
  }
}
</style>